import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Group, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';

import { INode } from '../Types/BulkPricingTypes';
import { IAdjustmentsProps } from './SurveyJobTypes';
import { IAppState } from '../../../../lib/types/AppState/index';
import { getFieldData, handleUpdateMatchFactors, standardizeDecimals } from '../utils';

const FOCUS = {
  ADJ: 'bulk-pricing-adj',
  GEO: 'bulk-pricing-geo',
  WGT: 'bulk-pricing-wgt',
  COMMENT: 'bulk-pricing-comment'
};

export default function Adjustments({
  aging,
  columns,
  dataCut,
  job,
  setIsEditing,
  setMatchDataLoading,
  surveyJob,
  updateMatchesInState,
}: IAdjustmentsProps) {
  const terms = surveyJob.terms;

  const [modifiedAdj, setModifiedAdj] = useState<number>(getFieldData('data_adjustment_factor_default', dataCut.data, terms) || 0);
  const [modifiedGeo, setModifiedGeo] = useState<number>(getFieldData('geo_adj_factor_default', dataCut.data, terms) || 0);
  const [modifiedWgt, setModifiedWgt] = useState<number>(getFieldData('data_weighting_factor_default', dataCut.data, terms) || 0);
  const [modifiedComment, setModifiedComment] = useState<string | number>(getFieldData('data_adjustment_factor_reason_default', dataCut.data, terms) || '');

  const benchmarkYear = useSelector((state: IAppState) => state.session.benchmarkYear);
  const editPastYears = useSelector((state: IAppState) => state.session.organization.accountMetaData.editPastYears);
  const editPastYearsGroup = useSelector((state: IAppState) => state.session.groupMetaData.editPastYears);
  const groupId = useSelector((state: IAppState) => state.session.organization.defaultGroup.id);
  const masterAccountID = useSelector((state: IAppState) => state.session.organization.masterAccountId);
  const orgId = useSelector((state: IAppState) => state.session.group.id);
  const displayIn = useSelector((state: IAppState) => state.session.monetary_display);

  const sameOrgAndGroup = orgId === groupId;

  const clearModifiedStates = () => {
    setModifiedAdj(getFieldData('data_adjustment_factor_default', dataCut.data, terms))
    setModifiedGeo(getFieldData('geo_adj_factor_default', dataCut.data, terms))
    setModifiedWgt(getFieldData('data_weighting_factor_default', dataCut.data, terms))
    setModifiedComment(getFieldData('data_adjustment_factor_reason_default', dataCut.data, terms))
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, dataCut: INode) => {
    if (!e.relatedTarget || (e.relatedTarget.id !== FOCUS.ADJ && e.relatedTarget.id !== FOCUS.GEO && e.relatedTarget.id !== FOCUS.WGT && e.relatedTarget.id !== FOCUS.COMMENT)) {
      saveMatch(dataCut);
      setIsEditing(false);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent, dataCut: INode) => {
    if (e.key === 'Escape') {
      clearModifiedStates();
      setIsEditing(false);
    } else if (e.key === 'Enter' && (modifiedAdj || modifiedGeo || modifiedWgt || modifiedComment)) {
      saveMatch(dataCut);
      setIsEditing(false);
    }
  };

  const isEditPastYears = () => (sameOrgAndGroup || !editPastYearsGroup ? editPastYears : editPastYearsGroup);

  const onSurveySave = async (updatedSurvey: any) => {
    if (!job) return;

    const match = {
      matchId: updatedSurvey.matchId,
      ...updatedSurvey.surveyAdjustmentFactors,
    };

    if (updateMatchesInState && setMatchDataLoading) {
      handleUpdateMatchFactors({
        orgJobId: job.id as number,
        matches: [match],
        benchmarkYear,
        editPastYears: isEditPastYears(),
        masterAccountID,
        currency: getFieldData('currency_default', dataCut?.data, terms) || '',
        job,
        updateMatchesInState,
        setMatchDataLoading,
        displayIn
      });
    }
  };

  const renderInput = (
    label: string,
    id: string,
    value: number,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    dataCut: INode
  ) => (
    <>
      <Text fw={700} size="s">{label}</Text>
      <TextInput
        type="float"
        id={id}
        step="0.001"
        defaultValue={standardizeDecimals(value)}
        onChange={onChange}
        onKeyUp={(e: React.KeyboardEvent) => handleKeyUp(e, dataCut)}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur(e, dataCut)}
        size="s"
        styles={{ input: { width: '50px', padding: "3px 5px" } }}
      />
    </>
  );

  const saveMatch = (dataCut: INode) => {
    onSurveySave({
      matchId: getFieldData('match_id_default', dataCut.data, terms),
      surveyAdjustmentFactors: {
        adj: modifiedAdj || 0,
        geo: modifiedGeo || 0,
        wgt: modifiedWgt || 0,
        comment: modifiedComment || "",
      },
    });
    clearModifiedStates();
  };

  return (
    <Table.Tr>
      <Table.Td colSpan={columns.length + 1}>
        <Group align="flex-start" mt="20px" mb="5px" ml="32px">
          <Stack>
            <Group>
              <Text className="datacut-weightings" fw={700} size="s">Aging</Text>
              <Text className="datacut-weightings" size="s">{`${aging}`}</Text>
              {renderInput('Adj', FOCUS.ADJ, modifiedAdj, (e) => setModifiedAdj(parseFloat(e.target.value)), dataCut)}
              {renderInput('Geo', FOCUS.GEO, modifiedGeo, (e) => setModifiedGeo(parseFloat(e.target.value)), dataCut)}
              {renderInput('Wgt', FOCUS.WGT, modifiedWgt, (e) => setModifiedWgt(parseFloat(e.target.value)), dataCut)}
            </Group>
            <Textarea
              className="bulk-pricing-comment"
              label="Comment"
              id={FOCUS.COMMENT}
              placeholder="Add Comment"
              defaultValue={modifiedComment}
              onChange={(e) => setModifiedComment(e.target.value)}
              onKeyUp={(e: React.KeyboardEvent) => handleKeyUp(e, dataCut)}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => handleBlur(e, dataCut)}
            />
            <Group>
              <Text c="var(--mantine-color-gravel-6)" size="xs" fw={700}>Enter</Text>
              <Text c="var(--mantine-color-gravel-6)" size="xs" fw={500}> save</Text>
              <Text c="var(--mantine-color-gravel-6)" size="xs" fw={700} ml="10px">Esc</Text>
              <Text c="var(--mantine-color-gravel-6)" size="xs" fw={500}> cancel</Text>
            </Group>
          </Stack>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
